import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "./Reveal";
import { TESTIMONIALS } from "@/data/content";

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const count = TESTIMONIALS.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = () => setIndex((i) => (i - 1 + count) % count);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const item = TESTIMONIALS[index];

  return (
    <section data-testid="testimonials-section" className="relative bg-[#0D0D0D] py-24 md:py-32 px-6 md:px-10 border-t border-[#C8A24A]/10">
      <div className="mx-auto max-w-4xl text-center">
        <Reveal>
          <p className="text-[#C8A24A] uppercase text-xs tracking-[0.35em] mb-4">Guest Love</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-14">
            Voices of <span className="italic text-gold-gradient">Ekaa</span>
          </h2>
        </Reveal>

        <div className="relative min-h-[300px] flex items-center justify-center">
          <Quote className="absolute -top-4 left-1/2 -translate-x-1/2 h-16 w-16 text-[#C8A24A]/15" />
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${index}`}
              className="px-4"
            >
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: item.rating }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-[#C8A24A] text-[#C8A24A]" />
                ))}
              </div>
              <p className="font-display text-2xl md:text-3xl leading-relaxed text-white italic">
                &ldquo;{item.text}&rdquo;
              </p>
              <p className="mt-8 text-[#C8A24A] font-medium tracking-wide">{item.name}</p>
              <p className="text-[#D6D6D6]/60 text-xs tracking-[0.2em] uppercase mt-1">{item.role}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-center gap-6 mt-10">
          <button onClick={prev} data-testid="testimonial-prev" className="h-11 w-11 flex items-center justify-center border border-[#C8A24A]/40 rounded-full text-[#C8A24A] hover:bg-[#C8A24A] hover:text-[#0D0D0D] transition-all">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-[#C8A24A]" : "w-2 bg-[#C8A24A]/30"}`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
          <button onClick={next} data-testid="testimonial-next" className="h-11 w-11 flex items-center justify-center border border-[#C8A24A]/40 rounded-full text-[#C8A24A] hover:bg-[#C8A24A] hover:text-[#0D0D0D] transition-all">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
